import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { CUSTOM_FIELDS_KEY } from "@/hooks/useCustomFields";

/**
 * Keeps custom field definitions fresh app-wide (#9399), like
 * useGlobalPipelinesSync. useCustomFields is staleTime: Infinity and read by
 * the list, the ticket page, the activity feed and Settings at once, so one
 * subscription here replaces one per reader.
 *
 * All three tables notify (0064): the field, its pick-list options, and the
 * departments it is switched on for. Values live on backlog_items and arrive
 * through its own sync.
 */
export const useGlobalCustomFieldsSync = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // One refetch per burst: saving a pick-list or deleting a field changes
    // many rows across the three tables, and each notifies on its own. 100 ms
    // trailing, like useBacklogItems batches its updates.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const invalidate = () => {
      clearTimeout(timer);
      timer = setTimeout(() => queryClient.invalidateQueries({ queryKey: CUSTOM_FIELDS_KEY }), 100);
    };

    const channel = supabase
      .channel("global-custom-fields-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "custom_fields" }, invalidate)
      .on("postgres_changes", { event: "*", schema: "public", table: "custom_field_options" }, invalidate)
      .on("postgres_changes", { event: "*", schema: "public", table: "custom_field_subsheets" }, invalidate)
      .subscribe();

    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};
